import React from 'react';
import { Link } from 'react-router-dom';
import { Table, Select, Typography, Space, Tag, Spin } from 'antd';

import { useAuth } from '../../authentication/AuthProvider';
import request from '../../lib/request';
import { useCompanyId } from '../../lib/books';
import { AttachmentsButton } from '../../components/AttachmentsPanel';
import { TASK_STATUS_COLORS, ChecklistProgress, DueDate } from './TasksPage';

import { date as fmtDate, label } from '../../lib/format';
/** Firm-wide list of Projects generated by Client Service Engagement activation, with expandable Task lists. */
export default function ProjectsPage() {
  const auth = useAuth();
  const isSystemAdmin = auth.user.role === 'SYSTEM_ADMIN';
  const { companyId, setCompanyId } = useCompanyId();
  const [companies, setCompanies] = React.useState([]);
  const [projects, setProjects] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [projectDetails, setProjectDetails] = React.useState({});

  React.useEffect(() => {
    if (isSystemAdmin) {
      request
        .get('/companies')
        .then((r) => r.json())
        .then((r) => {
          if (r.status) {
            setCompanies(r.data);
            if (!companyId && r.data.length) setCompanyId(r.data[0].id);
          }
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchProjects = () => {
    if (!companyId) return;
    setLoading(true);
    request
      .get('/projects', { company_id: companyId })
      .then((r) => r.json())
      .then((r) => r.status && setProjects(r.data))
      .finally(() => setLoading(false));
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  React.useEffect(fetchProjects, [companyId]);

  const loadDetail = (projectId) => {
    if (projectDetails[projectId]) return;
    request
      .get(`/projects/${projectId}`)
      .then((r) => r.json())
      .then((r) => {
        if (r.status) setProjectDetails((prev) => ({ ...prev, [projectId]: r.data }));
      });
  };

  const columns = [
    { title: 'Name', dataIndex: 'name' },
    { title: 'Status', dataIndex: 'status', render: (v) => <Tag color={v === 'OPEN' ? 'blue' : 'default'}>{label(v)}</Tag> },
    {
      title: 'Client',
      key: 'client',
      render: (_, p) => (p.client_id ? <Link to={`/clients/${p.client_id}`}>{p.Client ? p.Client.client_name : `Client ${p.client_id}`}</Link> : "This Company's own"),
    },
  ];

  return (
    <div>
      <Space style={{ marginBottom: 16, width: '100%', justifyContent: 'space-between' }}>
        <Typography.Title level={4} style={{ margin: 0 }}>
          Projects
        </Typography.Title>
        {isSystemAdmin && (
          <Select
            style={{ width: 200 }}
            value={companyId}
            placeholder="Company"
            onChange={setCompanyId}
            options={companies.map((c) => ({ value: c.id, label: c.name }))}
          />
        )}
      </Space>

      <Table
        rowKey="id"
        columns={columns}
        dataSource={projects}
        loading={loading}
        expandable={{
          expandedRowRender: (project) => {
            const detail = projectDetails[project.id];
            if (!detail) return <Spin size="small" />;
            return (
              <Table
                size="small"
                rowKey="id"
                pagination={false}
                dataSource={detail.Tasks || []}
                columns={[
                  { title: 'Task', dataIndex: 'subject', render: (v, task) => <Link to={`/tasks/${task.id}`}>{v}</Link> },
                  { title: 'Assigned', key: 'assigned', render: (_, task) => task.AssignedUser?.name || '-' },
                  { title: 'Start', dataIndex: 'exp_start_date', render: (v) => fmtDate(v) },
                  { title: 'Due', key: 'due', render: (_, task) => <DueDate task={task} /> },
                  { title: 'Checklist', key: 'checklist', render: (_, task) => <ChecklistProgress items={task.TaskChecklistItems} /> },
                  { title: 'Status', dataIndex: 'status', render: (v) => <Tag color={TASK_STATUS_COLORS[v]}>{label(v)}</Tag> },
                  { title: '', key: 'files', width: 100, render: (_, task) => <AttachmentsButton ownerType="TASK" ownerId={task.id} title={`Files - ${task.subject}`} /> },
                ]}
              />
            );
          },
          onExpand: (expanded, project) => {
            if (expanded) loadDetail(project.id);
          },
        }}
      />
    </div>
  );
}
